// @ts-strict-ignore
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { LMErrorUtilService, LMFormatUtilService, LMUrlUtilService } from '@services/utils/index';
import { LMSpecService } from '@services/lm-spec.service';
import { LMConfirmationMessageService, ConfirmationDialogResult } from '@modal/lm-confirmation-message.service';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMContextService } from '@services/lm-context.service';
import { AppConstants, LMContextUtilService, LMGlobalUtilService } from '@utils/index';
import { LMGlobalPersonalizationService } from '@states/services/lm-global-personalization.service';
import { LMModel } from '@form/model/lm-model';
import { LMRoamingUIProfileService, RoamingUIProfileResult, RoamingUIProfileResponse } from '@services/lm-roamingUIProfile.service';
import { LMWebAppService } from "@services/lm-webapp.service";

export class LMMenuEvent implements LMFieldEvent {
  link: string;
  event: Event;
  eventType: LMFieldEventType;

  constructor(event: Event, link: string) {
    this.eventType = 'MenuClickEvent';
    this.event = event;
    this.link = link;
  }
}

export type LMCardType = 'webapp' | 'menu' | 'registration' | 'signin' | 'signout';

@Component({
  selector:    'lm-simple-card',
  standalone: false,
  templateUrl: 'lm-simple-card.component.html',
  styleUrls: [ 'lm-simple-card.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ LMRoamingUIProfileService ],
  })
export class LMSimpleCardComponent implements OnInit {
  @HostBinding('class.card') card = false;

  @HostBinding('class.large-menu') largeMenu = false;

  @HostBinding('attr.data-automation-id') dataCardAutomationId = '';

  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;
  @Input() fromHomeSuite: boolean;

  @Input() automationId: string;
  @Input() type: LMCardType = 'webapp'; // 1 = webapp link, 2 = menu link
  @Input() scale: string;
  @Input() link: string;
  @Input() showBackground = true;
  @Input() imageUrl: string;
  @Input() title: LMMessageRule;
  @Input() description: LMMessageRule;
  @Input() paragraphLayout: boolean;
  @Input() preferred: boolean;
  @Input() webAppName: string;
  @Input() isLink: string;
  @Input() isExternalLink: string;
  @Input() allowScannableSearch: boolean;
  @Input() openWhenSingleResult: boolean;
  @Input() noAccess: boolean;

  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();
  @Output() anchorClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() preferredMenuEvent: EventEmitter<any> = new EventEmitter<any>();

  public confirmationMsg: string;
  public setPreferred: string = Soho.Locale.translate('SetPreferred');
  public removePreferred: string = Soho.Locale.translate('RemovePreferred');
  public preferredMsg: string = Soho.Locale.translate('Preferred');
  public globalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
  public isInAccessibilityMode: boolean;
  isMultiLineDescription: boolean;
  public mouseOverText: LMMessageRule;

  public cardViewClasses = '';
  public cardImageClasses = '';
  public inMingleWidget = false;
  public allowedToSetPreferred = false;

  constructor(private lmSpecService: LMSpecService,
    public lmUrlUtilService: LMUrlUtilService,
    public lmContextService: LMContextService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmWebAppService: LMWebAppService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmConfirmationMessageService: LMConfirmationMessageService,
    private lmLoggerService: LMLoggerService,
    private lmRoamingUIProfileService: LMRoamingUIProfileService,
    public  lmContextUtilService: LMContextUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    public lmFormatUtilService: LMFormatUtilService)
  {
    this.inMingleWidget = this.lmContextService.inMingleWidget();
  }

  ngOnInit() {
    this.isInAccessibilityMode = this.lmGlobalPersonalizationService.getAccessibilityMode();
    this.card = this.showBackground;
    this.largeMenu = !this.fromHomeSuite && this.isLargeScale;
    this.allowedToSetPreferred = this.lmContextService.allowedToSetPreferred() && !this.noAccess;
    this.mouseOverText = (this.description && !this.isLargeScale) ? this.description : this.title;

    if (this.automationId) {
      this.dataCardAutomationId = `lm-${this.automationId}-simple-card`;
    }
    this.buildAdditionalCardClasses();
  }

  onClickEvent(event: Event) {
    this.lmContextService.setClearNextPageState(true, false);

    if (this.isWebAppLink) {
      if (this.inMingleWidget) {
        let url = this.link;
        url += '&' + jQuery.param(this.lmContextService.getContext().contextSessionKeys, true);
        this.lmContextUtilService.sendDrillbackMessage(url);
      } else {
        // Let's make sure that the WebApp is loaded to ensure that
        // MenuSecurity data is ready when the main content area loads
        const webAppName =  this.lmUrlUtilService.getWebAppNameFromUrl(this.link);
        if (webAppName && webAppName !== 'web') {
          this.lmWebAppService.getWebAppMenuSpec(webAppName).subscribe(value => {
            this.lmGlobalUtilService.navigateByUrl(this.link);
            this.anchorClicked.emit(this.title);
          });
        } else {
          this.lmGlobalUtilService.navigateByUrl(this.link);
          this.anchorClicked.emit(this.title);
        }
      }
    } else if (this.inMingleWidget && this.type === 'menu') {
      const url = this.lmUrlUtilService.updateContextSessionKeyParams(this.link);
      this.lmContextUtilService.sendDrillbackMessage(url);
    } else {
      if (this.isLink === 'true') {
        if (this.type === 'registration') {
          this.lmGlobalUtilService.navigateByUrl(this.link);
        } else if (this.type === 'signin' || this.type === 'signout') {
          this.lmGlobalUtilService.navigate(this.link);
        } else {
          this.lmGlobalUtilService.navigateByUrl(this.link);
        }
      } else {
        this.fieldEvent.emit(new LMMenuEvent(event, this.link));
      }
    }
  }

  onFieldEvent(event: LMFieldEvent) {
    this.fieldEvent.emit(event);
  }

  onPreferredMenuEvent(event: Event) {
    event.stopImmediatePropagation();
    if (this.isPreferred) {
      this.confirmationMsg = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('RemovePreferredPage'), this.title);
      this.globalPersonalization.preferredWebApp = '';
      this.displayRemoveConfirmation();
    } else {
      this.confirmationMsg = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('DefaultLandingPage'), this.title);
      this.displayConfirmation(this.webAppName);
    }
  }

  public displayConfirmation(preferredWebApp: string) {
    this.lmConfirmationMessageService.promptConfirmationMessage({ message: this.confirmationMsg, okToAll: false })
      .subscribe(
        (result: any) => {
          if (result === ConfirmationDialogResult.CONFIRMATION_OK) {
            this.lmLoggerService.log([ 'Message confirmed: ', this.confirmationMsg ]);
            this.lmRoamingUIProfileService.setPreferredWebApp(preferredWebApp)
              .subscribe(
                (results: RoamingUIProfileResponse) => {
                  if (results.status === RoamingUIProfileResult.COMPLETED) {
                    this.globalPersonalization.preferredWebApp = preferredWebApp;
                    this.lmGlobalPersonalizationService.saveGlobalPersonalization(this.globalPersonalization);
                    this.lmGlobalUtilService.navigateByUrl(this.link);
                    this.anchorClicked.emit(this.title);
                  }
                });
          } else {
            this.lmLoggerService.log([ 'Message not confirmed: ', this.confirmationMsg ]);
          }
        }
      );
  }

  public displayRemoveConfirmation() {
    this.lmConfirmationMessageService.promptConfirmationMessage({ message: this.confirmationMsg, okToAll: false })
      .subscribe(
        (result: any) => {
          if (result === ConfirmationDialogResult.CONFIRMATION_OK) {
            this.lmLoggerService.log([ 'Message confirmed: ', this.confirmationMsg ]);
            this.lmGlobalPersonalizationService.saveGlobalPersonalization(this.globalPersonalization);
            this.preferredMenuEvent.emit({ preferredWebAppRemoved: true, webApp: this.webAppName });
          } else {
            this.lmLoggerService.log([ 'Message not confirmed: ', this.confirmationMsg ]);
          }
        }
      );
  }

  public onBarCodeClick() {
    this.lmContextService.setClearNextPageState(true, false);

    if (this.lmContextService.inNativeMobileApp() && !this.lmContextService.isMobileDevice()) {
      const test = '';
      this.processBarCodeClick(test);
    } else {
      this.lmContextUtilService.showQRCodeScanner().subscribe((response: LMBarCodeData) => {
        if (response?.data) {
          this.processBarCodeClick(response?.data);
        }
      });
    }
  }

  processBarCodeClick = (scanResults: string) => {

    const url = this.lmUrlUtilService.appendToQueryString(this.link, 'scanResults=' + scanResults);

    this.lmSpecService.getApplicationMenuItemSpec(url).subscribe(
    response => {
      if (response.redirectUrl) {
        this.lmGlobalUtilService.navigateByUrl(response.redirectUrl);
      }
    },
    error => {
      this.lmErrorUtilService.handleError(Soho.Locale.translate('BarCodeScanError'), null, null, Soho.Locale.translate('Error'));
      this.lmLoggerService.log('Error occurred while scanning');
    });
  };

  public get isParagraph(): boolean {
    return this.paragraphLayout;
  }

  public get isWebAppLink(): boolean {
    if (this.type === 'webapp') {
      return true;
    }
  }

  public get hasImage(): boolean {
    if (this.imageUrl !== '') {
      return true;
    }
  }

  public get isLargeScale(): boolean {
    if (this.scale !== '0') {
      return true;
    }
  }

  public get isPreferred(): boolean {
    return this.preferred;
  }

  public get showBarCode(): boolean {
    if (this.allowScannableSearch && this.lmContextService.inNativeMobileApp()) {
      return true;
    }

    return false;
  }

  public get smallMenuInDashboard(): boolean {
    if (this.model) {
      return this.model.formSpec.busFormSpec.implementsDashBoardView && this.scale === '0';
    }
    return false;
  }

  showToolTip(el: HTMLElement) {
    this.isMultiLineDescription = el.scrollHeight > el.clientHeight;
  }

  buildAdditionalCardClasses(): void {
    if (!this.isWebAppLink) {
      // lm-card-view additional classes
      this.cardViewClasses += this.smallMenuInDashboard ? ' smallMenuDashboard' : '';
      this.cardViewClasses += this.isParagraph? ' inline': '';
      this.cardViewClasses += this.fromHomeSuite? '': ' onPage';

      // lm-card-image additional classes
      if(!this.isLargeScale) {
        // description not supported on small icons
        this.cardImageClasses += ' smallMenuDashboardImage no-description';
      }
      this.cardImageClasses += !this.hasImage && !this.fromHomeSuite ? ' no-image': '';
      this.cardImageClasses += !this.isLargeScale && this.isParagraph ? ' small-image-paragraph': '';

    } else {
      // lm-card-view additional classes
      this.cardViewClasses += this.fromHomeSuite? '': ' onPage';

      // lm-card-image additional classes
      this.cardImageClasses += (!(!!this.description) && !this.isParagraph) && !this.fromHomeSuite ? ' no-description': '';
      this.cardImageClasses += !this.hasImage && !this.fromHomeSuite ? ' no-image': '';

    }
  }
}
